"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "MATERIAL SPECS"
}, {
  id: "m-02",
  index: "02",
  label: "ENGRAVING DEPTH"
}, {
  id: "m-03",
  index: "03",
  label: "MOUNTING GUIDES"
}, {
  id: "m-04",
  index: "04",
  label: "DPM PROTOCOLS"
}, {
  id: "m-05",
  index: "05",
  label: "FILE FORMATS"
}, {
  id: "m-06",
  index: "06",
  label: "QUALITY ASSURANCE"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "MATERIAL SPECS",
  instructions: ["Select industrial-grade phenolic for high-heat environments up to 300°F.", "Use UV-stable acrylics for outdoor signage requiring long-term color retention.", "Stainless steel 316 is recommended for corrosive marine or chemical processing zones.", "Anodized aluminum provides a lightweight, scratch-resistant finish for control panels.", "Verify material thickness against your specific mounting hardware requirements."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/material_spec_chart.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/material_spec_chart.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/material_spec_chart.png-wvc-sizes",
    alt: "Chart showing material heat and chemical resistance",
    tag: "[FIG. 01 / MATERIAL DATA]"
  },
  caution: "Ensure material compatibility with your specific environmental exposure."
}, {
  id: "m-02",
  index: "M-02",
  title: "ENGRAVING DEPTH",
  instructions: ["Standard depth for industrial tags is 0.015 inches for optimal readability.", "Deep engraving (0.030 inches) is required for high-wear or abrasive environments.", "Laser settings must be calibrated to the specific material density to prevent charring.", "Verify depth consistency across the entire surface area of the plate.", "Use a depth gauge to ensure compliance with project specifications."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_depth_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_depth_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_depth_diagram.png-wvc-sizes",
    alt: "Diagram showing cross-section of engraving depth",
    tag: "[FIG. 02 / DEPTH GAUGE]"
  },
  caution: "Excessive depth can compromise the structural integrity of thinner materials."
}, {
  id: "m-03",
  index: "M-03",
  title: "MOUNTING GUIDES",
  instructions: ["Use industrial-strength adhesive backing for smooth, clean surfaces.", "Mechanical fasteners (screws/rivets) are required for high-vibration equipment.", "Ensure mounting holes are drilled 1/8 inch from the edge to prevent cracking.", "Clean surfaces with isopropyl alcohol before applying adhesive-backed tags.", "Allow 24 hours for adhesive curing before subjecting to operational stress."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mounting_hardware_guide.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mounting_hardware_guide.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mounting_hardware_guide.png-wvc-sizes",
    alt: "Diagram of proper mounting techniques",
    tag: "[FIG. 03 / MOUNTING]"
  },
  caution: "Do not over-tighten mechanical fasteners on brittle phenolic materials."
}, {
  id: "m-04",
  index: "M-04",
  title: "DPM PROTOCOLS",
  instructions: ["Direct Part Marking (DPM) requires high-contrast laser settings for scanner readability.", "Ensure the surface is free of oils, grease, or debris prior to marking.", "Verify the Data Matrix code readability with a certified verifier.", "Maintain consistent laser focus distance to ensure uniform mark quality.", "Document all DPM settings for traceability and future reproduction."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_laser_setup.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_laser_setup.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_laser_setup.png-wvc-sizes",
    alt: "Diagram of DPM laser marking setup",
    tag: "[FIG. 04 / DPM SCAN]"
  },
  caution: "Verify mark contrast against ISO/IEC 16022 standards for DPM."
}, {
  id: "m-05",
  index: "M-05",
  title: "FILE FORMATS",
  instructions: ["Vector files (DXF, AI, EPS) are required for all precision engraving projects.", "Convert all text to outlines to ensure font compatibility during processing.", "Ensure all line weights are set to hairline or 0.001 inches for laser paths.", "Include a clear border or cut-line layer for custom shape fabrication.", "Submit files with a corresponding PDF proof for final design verification."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/file_format_guide.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/file_format_guide.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/file_format_guide.png-wvc-sizes",
    alt: "Guide for vector file preparation",
    tag: "[FIG. 05 / VECTOR]"
  },
  caution: "Raster images (JPG, PNG) are not suitable for precision laser engraving."
}, {
  id: "m-06",
  index: "M-06",
  title: "QUALITY ASSURANCE",
  instructions: ["Every tag undergoes a multi-point inspection for spelling, depth, and finish.", "Verify all serial numbers against the provided project manifest.", "Ensure protective masking is removed only after final installation.", "Check for surface burrs or debris on all cut edges before packaging.", "Maintain a sample archive for every production run for future reference."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/qa_inspection_process.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/qa_inspection_process.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/qa_inspection_process.png-wvc-sizes",
    alt: "Diagram of quality assurance inspection steps",
    tag: "[FIG. 06 / QA CHECK]"
  },
  caution: "Report any discrepancies in material or engraving within 48 hours of receipt."
}];
export default function FaqAccordion() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="faq-accordion" className="bg-background py-28 md:py-40 text-foreground">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-500 ease-out ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block size-2.5 bg-primary" aria-hidden="true" />
            <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              § 01 — Technical Manual
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
            <h1 className="font-default font-black uppercase leading-[0.88] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 8.2vw, 7.5rem)"
          }}>
              The{" "}
              <em className="not-italic md:italic font-black" style={{
              color: "var(--primary)",
              fontStyle: "italic"
            }}>
                Industrial
              </em>{" "}
              Engraving Guide.
            </h1>
            <div className="shrink-0">
              
            </div>
          </div>
        </div>

        <div className="mt-12 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px)",
        backgroundColor: "var(--background)",
        opacity: 0.85
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-mono text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border whitespace-nowrap transition-colors ${activeId === item.id ? "border-primary bg-primary text-primary-foreground" : "border-foreground/40 text-foreground hover:border-foreground"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-primary" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-primary pl-4" : "border-transparent pl-2 hover:border-foreground/40 hover:pl-3"}`}>
                        <span className="font-mono text-xs font-semibold tracking-[0.12em] text-muted-foreground shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-default font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-foreground last:border-b last:border-foreground data-[state=open]:border-l-4 data-[state=open]:border-l-primary transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        [{entry.index}]
                      </span>
                      <span className="font-default font-bold uppercase tracking-tight leading-none text-left text-foreground" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-primary text-primary-foreground justify-self-end shrink-0">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-card p-6 md:p-8 border border-foreground/15">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-primary" aria-hidden="true" />
                            Procedure
                          </div>
                          <ol className="space-y-3 font-default text-base leading-relaxed text-foreground">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-mono text-xs font-semibold tracking-[0.1em] text-muted-foreground pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-foreground/15">
                              <p className="font-mono text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-foreground">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "var(--primary)"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative">
                            <span className="absolute top-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-foreground/30" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              {entry.figure.tag}
                            </span>
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}